import { useMemo, useState } from 'react';
import { ChevronDown, Copy, Check, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { PropertyGroup } from '@/ifc/IFCLoader';
import { useViewer } from '@/features/viewer/ViewerContext';
import { useViewerStore } from '@/state/viewerStore';
import { cn } from '@/lib/cn';

export function PropertiesPanel() {
  const selection = useViewerStore((s) => s.selection);
  const properties = useViewerStore((s) => s.properties);
  const loading = useViewerStore((s) => s.propertiesLoading);
  const { deselect } = useViewer();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => filterProperties(properties, query), [properties, query]);
  const matchCount = useMemo(
    () => filtered.reduce((sum, g) => sum + g.properties.length, 0),
    [filtered],
  );

  if (selection.expressID === null) return null;

  return (
    <aside className="pointer-events-auto absolute top-16 right-3 bottom-14 z-30 flex w-80 flex-col overflow-hidden rounded-lg border border-panel-border bg-panel/90 shadow-2xl backdrop-blur">
      <header className="flex items-center justify-between border-b border-panel-border px-3 py-2">
        <div className="flex flex-col">
          <span className="text-sm font-semibold">Properties</span>
          <span className="text-xs text-muted">
            <code className="rounded bg-white/5 px-1.5 py-0.5 text-[11px]">#{selection.expressID}</code>
            {properties[0]?.name && (
              <span className="ml-2 text-accent">{properties[0].name}</span>
            )}
          </span>
        </div>
        <Button variant="ghost" size="icon" onClick={deselect} aria-label="Close properties">
          <X className="h-4 w-4" />
        </Button>
      </header>

      {!loading && properties.length > 0 && (
        <div className="border-b border-panel-border px-3 py-2">
          <div className="relative">
            <Search className="absolute top-1/2 left-2 h-3.5 w-3.5 -translate-y-1/2 text-muted" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter properties…"
              className="w-full rounded-md border border-panel-border bg-black/30 py-1 pr-2 pl-7 text-xs text-foreground placeholder:text-muted focus:border-accent focus:outline-none"
            />
          </div>
          {query && (
            <div className="mt-1.5 text-[11px] text-muted">
              {matchCount} {matchCount === 1 ? 'match' : 'matches'}
            </div>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-3 py-2">
        {loading ? (
          <div className="text-xs text-muted">Loading properties…</div>
        ) : filtered.length === 0 ? (
          <div className="text-xs text-muted">
            {query ? 'No properties match your filter.' : 'No properties available.'}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {filtered.map((group, i) => (
              <Group key={`${group.name}-${i}`} group={group} forceOpen={query.length > 0} />
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}

function filterProperties(groups: PropertyGroup[], query: string): PropertyGroup[] {
  if (!query.trim()) return groups;
  const q = query.toLowerCase();
  return groups
    .map((g) => ({
      ...g,
      properties: g.properties.filter(
        (p) => p.name.toLowerCase().includes(q) || p.value.toLowerCase().includes(q),
      ),
    }))
    .filter((g) => g.properties.length > 0 || g.name.toLowerCase().includes(q));
}

function Group({ group, forceOpen }: { group: PropertyGroup; forceOpen: boolean }) {
  const [open, setOpen] = useState(true);
  const isOpen = forceOpen || open;
  return (
    <section className="rounded-md border border-panel-border bg-black/20">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-2.5 py-1.5 text-left"
      >
        <span className="text-xs font-semibold tracking-wide text-accent uppercase">
          {group.name}
        </span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 text-muted transition-transform',
            !isOpen && '-rotate-90',
          )}
        />
      </button>
      {isOpen && (
        <dl className="grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)] gap-x-3 gap-y-0.5 border-t border-panel-border px-2.5 py-2 text-xs">
          {group.properties.map((p, i) => (
            <Row key={`${p.name}-${i}`} name={p.name} value={p.value} />
          ))}
        </dl>
      )}
    </section>
  );
}

function Row({ name, value }: { name: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = (): void => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    });
  };
  return (
    <>
      <dt className="truncate text-muted" title={name}>
        {name}
      </dt>
      <dd className="group flex items-start justify-between gap-1">
        <span className="break-words text-foreground">{value}</span>
        <button
          type="button"
          onClick={copy}
          title="Copy"
          aria-label={`Copy ${name}`}
          className="shrink-0 opacity-0 transition-opacity hover:text-accent group-hover:opacity-100"
        >
          {copied ? (
            <Check className="h-3 w-3 text-emerald-400" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </button>
      </dd>
    </>
  );
}
